import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Loader2, Tag, ShoppingCart, ArrowRight, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

export default function SecuritiesResale() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "" });

  useEffect(() => {
    axios.get(`${API}/securities/tradelines/resale/${token}`)
      .then((r) => setOffer(r.data))
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  const buy = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/securities/tradelines/resale/${token}/buy`, { origin_url: window.location.origin, ...form });
      window.location.href = data.url;
    } catch (err) { alert(errMsg(err)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="securities-resale-page">
      <div className="max-w-lg mx-auto">
        <Link to="/securities/marketplace" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]">← Marketplace</Link>
        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES · Tradeline Resale</div>
            <div className="text-xl font-serif mt-1">Purchase a resold tradeline</div>
          </div>
          {error ? (
            <div className="px-7 py-12 text-center" data-testid="resale-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !offer ? (
            <div className="px-7 py-16 text-center"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="resale-offer">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#C9A227]"><Tag size={13} /> {offer.instrument_type}</div>
              <div className="font-mono text-[13px] text-[#E4C97A] mt-2">{offer.tradeline_no}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Instrument face</div><div className="font-serif text-lg">{fmt(offer.face_amount)}</div></div>
                <div><div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Resale price</div><div className="font-serif text-lg text-[#3DBB8E]" data-testid="resale-price">{fmt(offer.resale_price)}</div></div>
              </div>
              <div className="text-[11px] text-[#8A8A8A] mt-2">Account {offer.account_no} · Seller {offer.seller_name || "—"}</div>
              <form onSubmit={buy} className="mt-6 space-y-3">
                <input required placeholder="Your full name" value={form.buyer_name} onChange={(e) => setForm((f) => ({ ...f, buyer_name: e.target.value }))} data-testid="resale-buyer-name"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                <input required type="email" placeholder="Your email" value={form.buyer_email} onChange={(e) => setForm((f) => ({ ...f, buyer_email: e.target.value }))} data-testid="resale-buyer-email"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5 text-[14px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                <button type="submit" data-testid="resale-pay" className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] py-3 rounded-md text-[12px] uppercase tracking-[0.16em] font-bold">Pay {fmt(offer.resale_price)} <ArrowRight size={15} /></button>
              </form>
              <p className="mt-5 text-[11px] text-[#6C6C64] leading-relaxed" data-testid="resale-disclaimer">
                This tradeline is a <strong>corporate credit-enhancement instrument</strong>, valid for 45 days and renewed by auto-debit. On payment, ownership transfers to you and a fresh certificate is reissued in your name. SEUTRADE FINANCIAL SERVICES is <strong>not a credit bureau</strong> and does not file with Dun &amp; Bradstreet or any bureau; submission of the generated records is the holder's responsibility.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
